import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import type { Filter } from '@cvent/venue-search-shared/types';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import dynamic from 'next/dynamic';
import { shouldVisible } from '@/utils/filters/filterHelper';
import { type RateHistoryLabels } from '@/utils/filters/getRateHistoryLabels';
import type { Locale } from '@/i18n';
import Loader from '@/components/filters/Loader';
import type { FeatureConfig } from '@/fetchers/getFeatureConfigs';
import { formatMessage } from '@/utils/translationHelper';
import useFullMap from '@/hooks/useFullMap';

const AccountDetailsFilter = dynamic(() => import('@/components/filters/AccountDetailsFilter'), {
  ssr: false,
  loading: () => <Loader />
});

type Props = {
  appliedFilters: Filter;
  applicableFilters: Array<string>;
  labels: {
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
    accountSettingsTitle: string;
    accountTagsTitle: string;
    cancelledSpaceTitle: string;
    lessTag: string;
    moreTag: string;
    cancelledSpaceLabel: string;
    cancelledSpaceFlyout: string;
    rateHistoryLabels: RateHistoryLabels;
  };
  locale: Locale;
  isLoggedIn: boolean;
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  featureConfig: FeatureConfig | null;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  testID?: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function AccountSettingsContainer({
  appliedFilters,
  applicableFilters,
  labels,
  locale,
  isLoggedIn,
  sortOptions,
  setSortingLabel,
  sort,
  setSort,
  setSortingType,
  sortType,
  searchTerm,
  featureConfig,
  setIsRfpBannerVisible,
  testID,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const {
    allFilters,
    resetAllFilters,
    cancelAllFilters,
    applyAllFilters,
    clear,
    apply,
    accountSettingsTitle,
    accountTagsTitle,
    cancelledSpaceTitle,
    lessTag,
    moreTag,
    cancelledSpaceLabel,
    cancelledSpaceFlyout,
    rateHistoryLabels
  } = labels;
  const [selected, setSelected] = useState<Filter>(appliedFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [filterCount, setFilterCount] = useState<number>(0);
  const { accountTagOptions } = useFullMap();

  useEffect(() => {
    setSelected(appliedFilters);

    // Break down filter count calculation
    const plannerTagsCount = appliedFilters.PLANNER_TAGS?.length || 0;
    const cancelledSpaceCount = appliedFilters.CANCELLED_SPACE ? 1 : 0;
    const rateHistoryCondition =
      appliedFilters.RATE_HISTORY &&
      (appliedFilters.RATE_HISTORY.maximum < 500 || appliedFilters.RATE_HISTORY.values.length < 12);
    const rateHistoryCount = rateHistoryCondition ? 1 : 0;

    const totalFilterCount = plannerTagsCount + cancelledSpaceCount + rateHistoryCount;
    setFilterCount(totalFilterCount);
  }, [appliedFilters]);

  return shouldVisible(['PLANNER_TAGS'], applicableFilters, selected) || isLoggedIn ? (
    <ApplyResetWrapper
      setIsSearchExpanded={setIsSearchExpanded}
      isSearchExpanded={isSearchExpanded}
      title={formatMessage(accountSettingsTitle, locale, {
        count: filterCount
      })}
      selected={selected}
      setSelected={setSelected}
      appliedFilters={appliedFilters}
      filters={['PLANNER_TAGS', 'CANCELLED_SPACE', 'RATE_HISTORY']}
      testID={testID}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
      showShadow
      setSortingLabel={setSortingLabel}
      setSortingType={setSortingType}
      sortOptions={sortOptions}
      sortType={sortType}
      sort={sort}
      setSort={setSort}
      searchTerm={searchTerm}
      filterCount={filterCount}
      setIsRfpBannerVisible={setIsRfpBannerVisible}
    >
      <AccountDetailsFilter
        labels={{
          accountTagsTitle,
          cancelledSpaceTitle,
          lessTag,
          moreTag,
          cancelledSpaceLabel,
          cancelledSpaceFlyout,
          rateHistoryLabels
        }}
        accountTagsOptions={accountTagOptions}
        selected={selected}
        setSelected={setSelected}
        appliedFilters={appliedFilters}
        locale={locale}
        applicableFilters={applicableFilters}
        isLoggedIn={isLoggedIn}
        featureConfig={featureConfig}
        testID={testID}
      />
    </ApplyResetWrapper>
  ) : (
    <></>
  );
}
